import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Camera, Loader2, Archive, KeyRound, User } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ONBOARDING_ROLES = [
    { id: "grandparent", label: "Grandparent" },
    { id: "parent", label: "Parent" },
    { id: "child", label: "Kid" },
    { id: "sibling", label: "Sibling" },
    { id: "other", label: "Family member" },
];

export default function Onboarding() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [checking, setChecking] = useState(true);
    const [stage, setStage] = useState("family");
    const [familyName, setFamilyName] = useState("");
    const [code, setCode] = useState("");
    const [loading, setLoading] = useState(null);
    const [error, setError] = useState("");
    const [photo, setPhoto] = useState(null);
    const [photoPreview, setPhotoPreview] = useState("");
    const [savingPhoto, setSavingPhoto] = useState(false);
    const [role, setRole] = useState("other");
    const photoInputRef = useRef(null);

    useEffect(() => {
        const check = async () => {
            try {
                await axios.get(`${API}/auth/me`, { withCredentials: true });
            } catch {
                navigate("/auth", { replace: true });
                return;
            }
            try {
                await axios.get(`${API}/families/mine`, { withCredentials: true });
                if (searchParams.get("photo") === "1") {
                    setStage("photo");
                } else {
                    navigate("/dashboard", { replace: true });
                    return;
                }
            } catch {}
            setChecking(false);
        };
        check();
    }, [navigate, searchParams]);

    const createFamily = async (e) => {
        e.preventDefault();
        setLoading("create");
        setError("");
        try {
            await axios.post(`${API}/families`, { name: familyName }, { withCredentials: true });
            setStage("photo");
        } catch (err) {
            setError(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Something went wrong.");
        }
        setLoading(null);
    };

    const joinFamily = async (e) => {
        e.preventDefault();
        setLoading("join");
        setError("");
        try {
            await axios.post(`${API}/families/join`, { code }, { withCredentials: true });
            setStage("photo");
        } catch (err) {
            setError(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Something went wrong.");
        }
        setLoading(null);
    };

    const onPhoto = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setPhoto(f);
        setPhotoPreview(URL.createObjectURL(f));
    };

    const finish = async (skip) => {
        setSavingPhoto(true);
        if (!skip && photo) {
            try {
                const form = new FormData();
                form.append("file", photo);
                await axios.post(`${API}/users/me/avatar`, form, { withCredentials: true });
            } catch {
                toast.error("Could not upload the photo — you can add it anytime on the Family page.");
            }
        }
        try {
            await axios.patch(`${API}/families/me`, { relationship: role }, { withCredentials: true });
        } catch {}
        setSavingPhoto(false);
        navigate("/dashboard", { replace: true });
    };

    if (checking) {
        return (
            <main data-testid="onboarding-loading" className="flex min-h-screen items-center justify-center bg-cream">
                <Loader2 size={32} className="animate-spin text-lavender" />
            </main>
        );
    }

    if (stage === "photo") {
        return (
            <main data-testid="onboarding-photo-step" className="flex min-h-screen items-center justify-center bg-cream px-6 py-16 text-navy">
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, ease: [0.76, 0, 0.24, 1] }}
                    className="w-full max-w-md text-center"
                >
                    <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.3em] text-[#C98A94]">
                        Step 3 of 3 · Your profile photo
                    </p>
                    <h1 className="mt-3 font-display text-3xl tracking-tight md:text-4xl">
                        Put a <span className="italic">face</span> to your stories.
                    </h1>
                    <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-[#2C3347]">
                        Your family will see this photo next to every memory, comment, and voice story you share.
                    </p>

                    <div className="relative mx-auto mt-8 h-32 w-32">
                        {photoPreview ? (
                            <img
                                src={photoPreview}
                                alt="Your profile photo"
                                data-testid="photo-preview"
                                className="h-32 w-32 rounded-full border-4 border-ivory object-cover shadow-lg shadow-navy/10"
                            />
                        ) : (
                            <div
                                data-testid="photo-placeholder"
                                className="flex h-32 w-32 items-center justify-center rounded-full border-4 border-ivory bg-gradient-to-br from-lavender to-peach shadow-lg shadow-navy/10"
                            >
                                <User size={44} className="text-white" />
                            </div>
                        )}
                        <button
                            data-testid="photo-pick-button"
                            onClick={() => photoInputRef.current?.click()}
                            aria-label="Choose a photo"
                            className="absolute bottom-0 right-0 flex h-10 w-10 items-center justify-center rounded-full bg-navy text-white shadow-lg transition-transform duration-300 hover:scale-110"
                        >
                            <Camera size={17} />
                        </button>
                    </div>
                    <input
                        ref={photoInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        data-testid="photo-file-input"
                        aria-label="Profile photo file"
                        onChange={onPhoto}
                    />
                    {photo && (
                        <p data-testid="photo-filename" className="mt-3 font-mono text-[11px] uppercase tracking-widest text-navy/60">
                            {photo.name}
                        </p>
                    )}

                    <div className="mt-8">
                        <p className="mb-2 font-mono text-[10px] uppercase tracking-widest text-navy/60">Your role in the family</p>
                        <div data-testid="onboarding-role-picker" className="flex flex-wrap justify-center gap-2">
                            {ONBOARDING_ROLES.map((r) => (
                                <button
                                    key={r.id}
                                    type="button"
                                    data-testid={`onboarding-role-${r.id}`}
                                    onClick={() => setRole(r.id)}
                                    className={`rounded-full border-2 px-4 py-2 text-xs font-semibold transition-colors duration-300 ${
                                        role === r.id ? "border-navy bg-navy text-white" : "border-warmgray text-navy hover:border-peach"
                                    }`}
                                >
                                    {r.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <button
                        data-testid="photo-continue-button"
                        onClick={() => finish(false)}
                        disabled={savingPhoto}
                        className="btn-gradient mt-8 flex w-full items-center justify-center gap-2 rounded-full py-4 text-sm font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60"
                    >
                        {savingPhoto && <Loader2 size={15} className="animate-spin" />}
                        {savingPhoto ? "Saving your photo…" : photo ? "Save photo & continue" : "Continue to your archive"}
                    </button>
                    <button
                        data-testid="photo-skip-button"
                        onClick={() => finish(true)}
                        disabled={savingPhoto}
                        className="mt-3 text-sm font-medium text-navy/70 underline-offset-4 transition-colors duration-300 hover:text-navy hover:underline"
                    >
                        Skip for now
                    </button>
                </motion.div>
            </main>
        );
    }

    return (
        <main data-testid="onboarding-page" className="flex min-h-screen items-center justify-center bg-cream px-6 py-16 text-navy">
            <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: [0.76, 0, 0.24, 1] }}
                className="w-full max-w-3xl"
            >
                <p className="text-center font-mono text-[11px] font-semibold uppercase tracking-[0.3em] text-[#C98A94]">
                    Step 2 of 3 · Your family archive
                </p>
                <h1 className="mt-3 text-center font-display text-3xl tracking-tight md:text-4xl">
                    Where do your family’s <span className="italic">stories live?</span>
                </h1>

                {error && <p data-testid="onboarding-error" className="mt-6 text-center text-sm text-red-700">{error}</p>}

                <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2">
                    <div className="rounded-3xl bg-gradient-to-br from-lavender to-peach p-[1.5px] shadow-xl shadow-navy/10">
                        <div className="flex h-full flex-col rounded-3xl bg-cream p-8">
                            <Archive size={26} className="text-lavender" />
                            <h2 className="mt-4 font-display text-2xl">Create a Family Archive</h2>
                            <p className="mt-2 flex-1 text-sm leading-relaxed text-navy">
                                Start a brand-new private archive for your family. You’ll get a family code to invite everyone you love.
                            </p>
                            <form onSubmit={createFamily} className="mt-6 space-y-3">
                                <input
                                    data-testid="create-family-name-input"
                                    type="text"
                                    value={familyName}
                                    onChange={(e) => setFamilyName(e.target.value)}
                                    placeholder="e.g. The Novák Family"
                                    aria-label="Family archive name"
                                    className="w-full rounded-full border-2 border-warmgray bg-[#FDFBF9] px-6 py-3.5 text-sm font-medium text-navy outline-none transition-colors duration-300 focus:border-peach"
                                />
                                <button
                                    data-testid="create-family-button"
                                    type="submit"
                                    disabled={loading === "create"}
                                    className="btn-gradient flex w-full items-center justify-center gap-2 rounded-full py-3.5 text-sm font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60"
                                >
                                    {loading === "create" && <Loader2 size={15} className="animate-spin" />}
                                    Create Archive
                                </button>
                            </form>
                        </div>
                    </div>

                    <div className="flex h-full flex-col rounded-3xl border-2 border-warmgray bg-ivory p-8 shadow-sm">
                        <KeyRound size={26} className="text-lavender" />
                        <h2 className="mt-4 font-display text-2xl">Join an Existing Family</h2>
                        <p className="mt-2 flex-1 text-sm leading-relaxed text-navy">
                            Someone in your family already started an archive? Enter the family code they shared with you.
                        </p>
                        <form onSubmit={joinFamily} className="mt-6 space-y-3">
                            <input
                                data-testid="join-family-code-input"
                                type="text"
                                required
                                value={code}
                                onChange={(e) => setCode(e.target.value.toUpperCase())}
                                placeholder="Family code, e.g. 9F3A2B"
                                aria-label="Family code"
                                className="w-full rounded-full border-2 border-warmgray bg-[#FDFBF9] px-6 py-3.5 font-mono text-sm font-medium uppercase tracking-widest text-navy outline-none transition-colors duration-300 focus:border-peach"
                            />
                            <button
                                data-testid="join-family-button"
                                type="submit"
                                disabled={loading === "join" || !code.trim()}
                                className="flex w-full items-center justify-center gap-2 rounded-full border-2 border-navy py-3.5 text-sm font-semibold text-navy transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60"
                            >
                                {loading === "join" && <Loader2 size={15} className="animate-spin" />}
                                Join Family
                            </button>
                        </form>
                    </div>
                </div>

                <p className="mt-8 text-center text-sm text-navy/70">
                    <Link to="/" data-testid="onboarding-back-home" className="font-medium underline-offset-4 hover:underline">Back Home</Link>
                </p>
            </motion.div>
        </main>
    );
}
